import { useMemo, useState } from "react";
import type { FormEvent } from "react";
import { Link, useSearchParams } from "react-router-dom";
import MovieCard from "@/components/feature/MovieCard";
import NewsListItem from "@/components/feature/NewsListItem";
import { movies } from "@/mocks/movies";
import { newsList } from "@/mocks/news";
import { useDocumentMeta } from "@/hooks/useDocumentMeta";

const tabs = [
  { key: "all", label: "全部" },
  { key: "电影", label: "电影" },
  { key: "电视剧", label: "电视剧" },
  { key: "资讯", label: "资讯" },
];

export default function Search() {
  const [params, setParams] = useSearchParams();
  const query = params.get("q") || "";
  const [keyword, setKeyword] = useState(query);
  const [tab, setTab] = useState("all");

  useDocumentMeta({
    title: query ? `“${query}”的搜索结果 - 八戒影视` : "站内搜索 - 八戒影视",
    description: `在八戒影视搜索“${query || "影视作品与资讯"}”，快速找到相关影片、剧集与影视资讯内容。`,
    keywords: `八戒影视,搜索,${query || "影视搜索"}`,
  });

  const onSubmit = (event: FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    const value = keyword.trim();
    setParams(value ? { q: value } : {});
    setTab("all");
  };

  const matchedMovies = useMemo(() => {
    if (!query) return [];
    const q = query.toLowerCase();
    return movies.filter(
      (m) =>
        m.title.toLowerCase().includes(q) ||
        m.actors.some((a) => a.toLowerCase().includes(q)) ||
        m.director.toLowerCase().includes(q) ||
        m.genres.some((g) => g.toLowerCase().includes(q))
    );
  }, [query]);

  const matchedNews = useMemo(() => {
    if (!query) return [];
    const q = query.toLowerCase();
    return newsList.filter((n) => n.title.toLowerCase().includes(q) || n.summary.toLowerCase().includes(q));
  }, [query]);

  const movieResults = tab === "资讯" ? [] : matchedMovies.filter((m) => tab === "all" || m.type === tab);
  const newsResults = tab === "电影" || tab === "电视剧" ? [] : matchedNews;
  const totalCount = movieResults.length + newsResults.length;
  const hasQuery = query.trim().length > 0;

  return (
    <div className="max-w-7xl mx-auto px-4 md:px-6 py-8">
      <div className="max-w-3xl mx-auto text-center mb-8">
        <h1 className="text-2xl md:text-3xl font-bold text-foreground-950">站内搜索</h1>
        <p className="text-sm text-foreground-500 mt-2">输入影片名称、演员、导演或资讯关键词，快速查找你喜欢的内容。</p>
        <form onSubmit={onSubmit} className="relative mt-5">
          <i className="ri-search-line absolute left-4 top-1/2 -translate-y-1/2 text-foreground-400"></i>
          <input
            value={keyword}
            onChange={(e) => setKeyword(e.target.value)}
            placeholder="例如：悬疑、星际边缘、医者仁心"
            className="w-full pl-11 pr-24 py-3.5 text-sm rounded-lg bg-background-100 border border-background-300 text-foreground-800 outline-none focus:border-primary-400 focus:ring-2 focus:ring-primary-100"
          />
          <button
            type="submit"
            className="absolute right-2 top-1/2 -translate-y-1/2 px-5 py-2 rounded-md bg-primary-500 hover:bg-primary-600 text-background-50 text-sm font-medium transition-colors whitespace-nowrap cursor-pointer"
          >
            搜索
          </button>
        </form>
      </div>

      {hasQuery && (
        <>
          <div className="flex flex-wrap items-center gap-2 border-b border-background-200 mb-6">
            {tabs.map((t) => (
              <button
                key={t.key}
                type="button"
                onClick={() => setTab(t.key)}
                className={`px-4 py-2.5 text-sm -mb-px border-b-2 transition-colors cursor-pointer ${
                  tab === t.key
                    ? "border-primary-500 text-primary-600 font-medium"
                    : "border-transparent text-foreground-500 hover:text-foreground-800"
                }`}
              >
                {t.label}
              </button>
            ))}
          </div>

          <p className="text-sm text-foreground-500 mb-6">
            关于 <strong className="text-foreground-900">“{query}”</strong> 的搜索结果：共找到{" "}
            <strong className="text-primary-600">{totalCount}</strong> 条内容
          </p>

          {totalCount > 0 ? (
            <div className="space-y-10">
              {movieResults.length > 0 && (
                <section>
                  <h2 className="text-lg font-bold text-foreground-950 mb-4 flex items-center gap-2">
                    <span className="w-1 h-5 rounded-full bg-primary-500"></span>
                    影视作品（{movieResults.length}）
                  </h2>
                  <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 gap-3 md:gap-4">
                    {movieResults.map((movie) => (
                      <MovieCard key={movie.id} movie={movie} />
                    ))}
                  </div>
                </section>
              )}

              {newsResults.length > 0 && (
                <section>
                  <h2 className="text-lg font-bold text-foreground-950 mb-4 flex items-center gap-2">
                    <span className="w-1 h-5 rounded-full bg-accent-500"></span>
                    影视资讯（{newsResults.length}）
                  </h2>
                  <div className="rounded-lg border border-background-200 bg-background-50 px-4 md:px-5">
                    {newsResults.map((item) => (
                      <NewsListItem key={item.id} item={item} />
                    ))}
                  </div>
                </section>
              )}
            </div>
          ) : (
            <div className="flex flex-col items-center justify-center text-center py-16 px-4 rounded-lg border border-dashed border-background-300 bg-background-100">
              <span className="w-16 h-16 rounded-full bg-background-200 flex items-center justify-center text-foreground-400 mb-4">
                <i className="ri-search-eye-line text-3xl"></i>
              </span>
              <h2 className="text-lg font-bold text-foreground-900">没有找到与“{query}”相关的内容</h2>
              <p className="text-sm text-foreground-500 mt-2 max-w-md">
                试试更换关键词，或使用以下推荐词：悬疑、科幻、都市、古装。也可以返回首页浏览热门推荐。
              </p>
              <div className="flex flex-wrap justify-center gap-2 mt-4">
                {["悬疑", "科幻", "都市", "古装"].map((word) => (
                  <button
                    key={word}
                    type="button"
                    onClick={() => {
                      setKeyword(word);
                      setParams({ q: word });
                      setTab("all");
                    }}
                    className="px-3 py-1.5 rounded-full bg-secondary-100 text-secondary-800 text-sm hover:bg-secondary-200 transition-colors cursor-pointer"
                  >
                    {word}
                  </button>
                ))}
              </div>
              <Link
                to="/"
                className="mt-6 px-5 py-2.5 rounded-md bg-primary-500 hover:bg-primary-600 text-background-50 text-sm font-medium transition-colors cursor-pointer"
              >
                返回首页
              </Link>
            </div>
          )}
        </>
      )}

      {!hasQuery && (
        <div className="flex flex-col items-center justify-center text-center py-16 px-4 rounded-lg border border-dashed border-background-300 bg-background-100">
          <span className="w-16 h-16 rounded-full bg-background-200 flex items-center justify-center text-foreground-400 mb-4">
            <i className="ri-search-line text-3xl"></i>
          </span>
          <h2 className="text-lg font-bold text-foreground-900">开始你的影视探索</h2>
          <p className="text-sm text-foreground-500 mt-2 max-w-md">
            在上方输入关键词，搜索影视作品、演员、导演或影视资讯。热门推荐：悬疑、科幻、古装、都市。
          </p>
          <div className="flex flex-wrap justify-center gap-2 mt-4">
            {["悬疑", "科幻", "古装", "都市"].map((word) => (
              <button
                key={word}
                type="button"
                onClick={() => {
                  setKeyword(word);
                  setParams({ q: word });
                }}
                className="px-3 py-1.5 rounded-full bg-secondary-100 text-secondary-800 text-sm hover:bg-secondary-200 transition-colors cursor-pointer"
              >
                {word}
              </button>
            ))}
          </div>
        </div>
      )}
    </div>
  );
}